import { History as HistoryIcon, SquareStack } from 'lucide-react';
import type { ResourceMeta, SummaryView } from '../../types/resource';
import type { PaginatedSubmissions, SubmissionRow } from '../../types/submission';

interface ResourceSummaryCardsProps {
  meta: ResourceMeta;
  page: PaginatedSubmissions | null;
  loading: boolean;
  // Edit intentionally omitted — `?summary=true` responses lack updated_at,
  // so PATCH would be rejected as a conflict. View opens the drawer instead.
  onViewRow?: (row: SubmissionRow) => void;
  onHistoryRow?: (row: SubmissionRow) => void;
}

export function ResourceSummaryCards({
  meta,
  page,
  loading,
  onViewRow,
  onHistoryRow,
}: ResourceSummaryCardsProps) {
  const summaryView = meta.view_meta?.summary_view ?? {};
  const rows = page?.data ?? [];

  if (loading && rows.length === 0) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rounded-xl border border-rgf-gray-200 bg-white p-4 shadow-sm">
            <div className="h-4 w-1/3 animate-pulse rounded bg-rgf-gray-200" />
            <div className="mt-3 h-3 w-1/2 animate-pulse rounded bg-rgf-gray-100" />
            <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-rgf-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-rgf-gray-200 bg-white p-12 text-center text-sm text-rgf-gray-500">
        No submissions match the current filters.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {rows.map((row) => (
        <SummaryCard
          key={String(row.id ?? Math.random())}
          row={row}
          summary={summaryView}
          onView={onViewRow ? () => onViewRow(row) : undefined}
          onHistory={onHistoryRow ? () => onHistoryRow(row) : undefined}
        />
      ))}
    </div>
  );
}

function SummaryCard({
  row,
  summary,
  onView,
  onHistory,
}: {
  row: SubmissionRow;
  summary: SummaryView;
  onView?: () => void;
  onHistory?: () => void;
}) {
  // `?summary=true` returns remapped `title`/`subtitle`/`summary`/`image` keys;
  // fall through to source-field names if the server hands us a full row.
  const title =
    pickString(row, 'title') ??
    pickString(row, summary.title) ??
    String(row.id ?? '—');
  const subtitle =
    pickString(row, 'subtitle') ?? pickString(row, summary.subtitle);
  const image = pickString(row, 'image') ?? pickString(row, summary.image);

  const summaryLine = pickString(row, 'summary');
  const summaryValues = summaryLine
    ? [summaryLine]
    : (summary.summary ?? [])
        .map((key) => pickString(row, key))
        .filter((v): v is string => Boolean(v && v.trim()));

  return (
    <article className="group relative flex items-start gap-3 rounded-xl border border-rgf-gray-200 bg-white p-4 shadow-sm transition hover:border-rgf-primary-200 hover:shadow">
      {image ? (
        <img
          src={image}
          alt=""
          className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-rgf-primary-50 text-lg font-semibold uppercase text-rgf-primary-700">
          {initialsFor(title)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-medium text-rgf-gray-900">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 truncate text-xs text-rgf-gray-500">{subtitle}</p>
        )}
        {summaryValues.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {summaryValues.map((v, i) => (
              <span
                key={`${v}-${i}`}
                className="inline-flex items-center rounded-full bg-rgf-gray-100 px-2 py-0.5 text-xs text-rgf-gray-600"
              >
                {v}
              </span>
            ))}
          </div>
        )}
      </div>
      {(onView || onHistory) && (
        <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          {onHistory && (
            <button
              type="button"
              onClick={onHistory}
              className="rounded p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
              title="View change history"
            >
              <HistoryIcon className="h-4 w-4" />
            </button>
          )}
          {onView && (
            <button
              type="button"
              onClick={onView}
              className="rounded p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-primary-700"
              title="View details (edit available inside)"
            >
              <SquareStack className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function pickString(row: SubmissionRow, key: string | undefined): string | undefined {
  if (!key) return undefined;
  const raw = row[key];
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.name === 'string') return obj.name;
    if (typeof obj.title === 'string') return obj.title;
  }
  return undefined;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase() || '?';
}
